import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ConfirmationModal } from 'components/organisms/modals/confirmation-modal'
import { STRIPE_CONNECTED_CALLBACK_KEY } from 'utils/constants'

export interface StripeSetupWarningModalProps {
	open: boolean
	setOpen: (open: boolean) => void
	/** If true, user has connected an account but may need to complete setup. Used for button label context. Always navigates to the Stripe account page. */
	hasConnectAccount: boolean
	/** Controls the context-specific wording. Defaults to 'ticket'. */
	type?: 'ticket' | 'offer'
	/**
	 * Whether the current user is the Producer (event creator) — the only person who can clear this
	 * block by connecting their own Stripe account. Defaults to true to preserve the original
	 * single-audience behavior for contexts with no Producer/non-Producer distinction (e.g. offers).
	 */
	isProducer?: boolean
	/** Producer's display name, shown to non-Producers so they know who to contact. Optional. */
	producerName?: string
}

export function StripeSetupWarningModal({
	open,
	setOpen,
	hasConnectAccount,
	type = 'ticket',
	isProducer = true,
	producerName,
}: StripeSetupWarningModalProps) {
	const navigate = useNavigate()
	const location = useLocation()

	const noun = type === 'offer' ? 'offers' : 'tickets'

	const handleConfirm = () => {
		setOpen(false)
		// Pass the current location so the Stripe page can return here after a successful setup.
		// forceRestoreDraft=true tells the create page to restore silently (no popup) on return.
		const params = new URLSearchParams(location.search)
		params.set('forceRestoreDraft', 'true')
		const returnUrl = `${location.pathname}?${params.toString()}`
		// Belt-and-suspenders: also stash it so it survives the Stripe OAuth redirect round-trip
		// even if the query param is dropped before the Stripe page mounts.
		try {
			sessionStorage.setItem(STRIPE_CONNECTED_CALLBACK_KEY, returnUrl)
		} catch {
			// ignore storage failures
		}
		navigate(`/manage/rep-clubs/events/revenue/stripe?connected_callback_url=${encodeURIComponent(returnUrl)}`)
	}

	// Non-Producers have no payout setup of their own to complete — the account that must be
	// connected belongs to the Producer. Sending them to "Go to profile" would connect Stripe
	// against the wrong account and leave the real block unresolved, so we show a read-only notice
	// with a single dismiss button and no navigation.
	if (!isProducer) {
		return (
			<ConfirmationModal
				title="Stripe payout setup required"
				open={open}
				setOpen={setOpen}
				onConfirm={() => setOpen(false)}
				confirmLabel="Got it"
			>
				<div className="mb-4">
					{`Paid ${noun} can't be added until this event's Producer${
						producerName ? ` (${producerName})` : ''
					} has connected and activated their Stripe account. Please contact the Producer to complete the setup.`}
				</div>
			</ConfirmationModal>
		)
	}

	return (
		<ConfirmationModal
			title="Stripe payout setup required"
			open={open}
			setOpen={setOpen}
			onConfirm={handleConfirm}
			onCancel={() => setOpen(false)}
			confirmLabel={hasConnectAccount ? 'Continue Stripe setup' : 'Go to profile'}
			cancelLabel="Cancel"
		>
			<div className="mb-4">
				To add paid {noun}, you need to complete your Stripe payout setup. Please finish connecting and
				activating your Stripe account before creating paid {noun}.
			</div>
		</ConfirmationModal>
	)
}

// ---------------------------------------------------------------------------
// Modal Manager integration
// ---------------------------------------------------------------------------

export interface StripeSetupWarningModalManagedProps {
	hasConnectAccount: boolean
	type?: 'ticket' | 'offer'
	isProducer?: boolean
	producerName?: string
}

declare module 'components/organisms/modals/modal-registry' {
	interface ModalRegistry {
		'stripe-setup-warning': StripeSetupWarningModalManagedProps
	}
}

export function ManagedStripeSetupWarningModal({
	onClose,
	...rest
}: StripeSetupWarningModalManagedProps & { onClose: () => void }) {
	const [open, setOpen] = useState(true)

	const handleClose = () => {
		setOpen(false)
		setTimeout(onClose, 200)
	}

	return (
		<StripeSetupWarningModal
			open={open}
			setOpen={(v: boolean) => { if (!v) handleClose() }}
			{...rest}
		/>
	)
}
